import { useEffect, useRef, useState } from "react";
import { notificationApi } from "../../api/notificationApi";
import { getAccessToken, getTenantId } from "../../services/authSession";
import { toAlert, toAlertList } from "./notificationAdapters";

const MAX_ALERTS = 50;
let initialAlertsRequest = null;

const loadInitialAlerts = () => {
  if (!initialAlertsRequest) {
    initialAlertsRequest = notificationApi
      .list({ page: 1, limit: MAX_ALERTS })
      .then(toAlertList)
      .finally(() => {
        initialAlertsRequest = null;
      });
  }
  return initialAlertsRequest;
};

const mergeAlerts = (current, incoming) => {
  const records = [...incoming, ...current];
  const seen = new Set();
  return records.filter((alert) => {
    if (seen.has(alert.id)) return false;
    seen.add(alert.id);
    return true;
  }).slice(0, MAX_ALERTS);
};

const getStreamUrl = () => {
  const base = process.env.REACT_APP_API_BASE_URL || "/tracesync/";
  return `${base.replace(/\/$/, "")}/notifications/stream/Notification`;
};

export default function useNotificationStream() {
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const retryTimer = useRef(null);

  const markAsRead = async (alertId) => {
    const alert = alerts.find((item) => item.id === alertId);
    if (!alert || alert.isRead) return;

    setAlerts((current) => current.map((item) => item.id === alertId ? { ...item, isRead: true } : item));
    try {
      await notificationApi.markAsRead(alertId);
    } catch (requestError) {
      setAlerts((current) => current.map((item) => item.id === alertId ? { ...item, isRead: false } : item));
      setError(requestError);
    }
  };

  useEffect(() => {
    let controller;
    let active = true;

    const connect = async () => {
      controller = new AbortController();
      let idleTimer;
      let reader;
      const resetIdleTimer = () => {
        window.clearTimeout(idleTimer);
        idleTimer = window.setTimeout(() => controller.abort(), 45000);
      };
      try {
        resetIdleTimer();
        const accessToken = getAccessToken();
        const tenantId = getTenantId();
        const response = await fetch(getStreamUrl(), {
          headers: {
            Accept: "text/event-stream",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            ...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
          },
          signal: controller.signal,
        });
        if (!response.ok || !response.body) throw new Error("Notification stream unavailable");

        reader = response.body.getReader();
        setError(null);
        const decoder = new TextDecoder();
        let buffer = "";
        while (active) {
          const { done, value } = await reader.read();
          if (done) break;
          resetIdleTimer();
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split(/\r?\n\r?\n/);
          buffer = events.pop() || "";
          events.forEach((eventBlock) => {
            const data = eventBlock
              .split(/\r?\n/)
              .filter((line) => line.startsWith("data:"))
              .map((line) => line.slice(5).trim())
              .join("\n");
            if (!data) return;
            try {
              setAlerts((current) => mergeAlerts(current, [toAlert(JSON.parse(data))]));
              setIsLoading(false);
              setError(null);
            } catch {
              // Ignore malformed events and keep the stream alive.
            }
          });
        }
      } catch (streamError) {
        if (active) setError(streamError);
      } finally {
        window.clearTimeout(idleTimer);
        if (reader) {
          await reader.cancel().catch(() => {});
          reader.releaseLock();
        }
        // EOF is a disconnect too. Retry with a fresh controller after an
        // idle timeout, network failure, or normal server shutdown.
        if (active) retryTimer.current = window.setTimeout(connect, 5000);
      }
    };

    const start = async () => {
      connect();
      try {
        const existingAlerts = await loadInitialAlerts();
        if (active) {
          // Preserve events that arrived while the history request was in
          // flight, while still removing records present in both sources.
          setAlerts((current) => mergeAlerts(existingAlerts, current));
        }
      } catch (requestError) {
        if (active) setError(requestError);
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    start();
    return () => {
      active = false;
      controller?.abort();
      window.clearTimeout(retryTimer.current);
    };
  }, []);

  return { alerts, isLoading, error, markAsRead };
}
